import { P } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3 } from '..';
import {
  type MultiSelectGroup,
  type MultiSelectOption,
  type OnChangeRequest,
  useMultiSelect,
} from '../hooks/useMultiSelect';
import type { SelectGroup } from '../hooks/useSelect';
import { FloatingPopover } from './FloatingPopover';
import { ScrollArea } from './ScrollArea';
import {
  OptionGroup,
  type OptionVariant,
  SelectButton,
  type SelectButtonVariant,
  SelectCheck,
  SelectComponents,
  SelectContainer,
  SelectInput,
  SelectItem,
  TagOption,
  TagValue,
  TextOption,
  TextValue,
} from './Select';

export type { MultiSelectGroup, MultiSelectOption, OnChangeRequest, SelectGroup };
export { SelectCheck };

export interface MultiSelectComponents<A, B = unknown> {
  Loading?: () => React.ReactElement | null;
  Error?: (props: { error: Error }) => React.ReactElement | null;
  Value?: (props: {
    selectedValues: MultiSelectOption<A>[];
    option: MultiSelectOption<A>;
    onRemove: () => void;
    index: number;
  }) => React.ReactElement | null;
  Placeholder?: (props: { placeholder?: string }) => React.ReactElement | null;
  NoOptions?: () => React.ReactElement | null;
  Option?: (props: { option: MultiSelectOption<A> }) => React.ReactElement | null;
  OptionGroup?: (props: { group: MultiSelectGroup<A, B> }) => React.ReactElement | null;
  Header?: () => React.ReactElement | null;
  Footer?: () => React.ReactElement | null;
}

export const MultiSelectComponents = SelectComponents;

const getComponents = <A, B = unknown>(
  components: Partial<MultiSelectComponents<A, B>>,
  optionVariant: OptionVariant,
) => {
  return {
    ...SelectComponents,
    Option: optionVariant === 'tag' ? TagOption : TextOption,
    Value: optionVariant === 'tag' ? TagValue : TextValue,
    OptionGroup,
    Footer: () => <></>,
    Header: undefined,
    ...components,
  };
};

export interface MultiSelectProps<A, B = unknown> extends Omit<ChakraV3.BoxProps, 'onChange'> {
  options: MultiSelectOption<A>[] | MultiSelectGroup<A, B>[];
  selectedValues: MultiSelectOption<A>[];
  inputValue?: string;
  variant?: SelectButtonVariant;
  size?: ChakraV3.ButtonProps['size'];
  optionVariant?: OptionVariant;
  mode?: 'single' | 'multiple';
  placeholder?: string;
  isLoading?: boolean;
  error?: Error | null;
  isCreatable?: boolean;
  isDisabled?: boolean;
  showArrow?: boolean;
  components?: MultiSelectComponents<A, B>;
  onChange: (req: OnChangeRequest<A>) => void;
  onInputChange?: (value: string) => void;
  onOpen?: () => void;
}

export const MultiSelect = <A, B = unknown>(props: MultiSelectProps<A, B>) => {
  const {
    options,
    selectedValues,
    inputValue,
    placeholder,
    isLoading,
    error,
    isCreatable,
    isDisabled,
    components,
    variant = 'selectOutline',
    mode = 'multiple',
    showArrow = true,
    optionVariant = 'text',
    size = 'sm',
    onChange,
    onInputChange,
    onOpen,

    ...rest
  } = props;

  const select = useMultiSelect<A, B>({
    isCreatable,
    mode,
    options,
    selectedValues,
    inputValue,
    isDisabled,
    onChange,
    onInputChange,
    onOpen,
  });

  const Components = getComponents(components ?? {}, optionVariant);

  const renderOption = (option: MultiSelectOption<A>) => (
    <SelectItem key={option.value} {...select.optionProps(option)}>
      {Components.Option({ option })}
    </SelectItem>
  );

  const renderGroup = (group: MultiSelectGroup<A, B>) => {
    if (group.options.length === 0) {
      return null;
    }

    return (
      <ChakraV3.Box key={group.id}>
        {Components.OptionGroup({ group })}
        <ChakraV3.Box pl={1}>{group.options.map(renderOption)}</ChakraV3.Box>
      </ChakraV3.Box>
    );
  };

  return (
    <ChakraV3.Box className='group' {...select.menuProps()} w='full' {...rest}>
      <FloatingPopover
        open={select.isOpen}
        enabled={!isDisabled}
        placement='bottom-start'
        isRoot={false}
        trigger='focus'
        render={() => (
          <SelectContainer>
            <ChakraV3.Box borderBottomWidth='1px'>
              <SelectInput {...select.inputProps()} />
            </ChakraV3.Box>
            <ScrollArea maxH='xs' overflow='auto'>
              <ChakraV3.chakra.ul p={1.5} data-pw='menu'>
                {error && Components.Error({ error })}
                {isLoading && !error && P.isEmpty(select.filteredOptions) && Components.Loading()}
                {!isLoading &&
                  !error &&
                  P.isEmpty(select.filteredOptions) &&
                  Components.NoOptions()}

                {select.isGrouped
                  ? select.filteredGroups.map(renderGroup)
                  : select.filteredOptions.map(renderOption)}
              </ChakraV3.chakra.ul>
            </ScrollArea>
            {Components.Footer()}
          </SelectContainer>
        )}
      >
        <ChakraV3.Box tabIndex={-1}>
          <SelectButton
            {...select.buttonProps()}
            variant={variant}
            size={size}
            showArrow={showArrow}
            isDisabled={isDisabled}
            isLoading={isLoading}
          >
            <ChakraV3.Wrap gap={1} w='full'>
              {P.isEmpty(select.selectedValues)
                ? Components.Placeholder({ placeholder })
                : select.selectedValues.map((option, index) => (
                    <ChakraV3.WrapItem key={option.value} data-pw={`selected-${option.value}`}>
                      {Components.Value({
                        selectedValues: select.selectedValues,
                        option,
                        onRemove: () => select.selectOption(option),
                        index,
                      })}
                    </ChakraV3.WrapItem>
                  ))}
            </ChakraV3.Wrap>
          </SelectButton>
        </ChakraV3.Box>
      </FloatingPopover>
    </ChakraV3.Box>
  );
};
